/**
 * QUARTOS/SUÍTES/BANHEIROS/VAGAS COM VÁRIOS NÚMEROS — filtro de /imoveis do modelo #9 Atelier.
 *
 * Pedido do Daniel em 20/10/2026: no Atelier (topo 12) os botões 1·2·3·4·5+ da lateral marcam
 * MAIS DE UM número (ex.: 2 e 3 quartos). O estado de sempre continua: `filters.dormitorios_min`
 * é o número EXATO (5 = "5 ou mais"). Com 2+ marcados ele guarda o MENOR — a IA, o noindex e o
 * "tem filtro?" seguem lendo dali — e a lista inteira vai em `filters.dormitorios_lista`.
 *
 * Contrato (só com 2+ marcados; com 1 vale o de sempre):
 *  - URL:   `dormitorios=2,3` (idem suites/banheiros/vagas)
 *  - busca: `dormitorios_lista: [2, 3]` no site-graphql (102e40e) no lugar do par min/max
 * Fora do Atelier nada daqui entra: os botões marcam um número só e a URL nunca tem vírgula.
 *
 * Daniel 20/10 (2ª rodada): "tem que adicionar na home também em todos os modelos novos" — o
 * múltiplo vale nos 5 modelos novos (`qtdMultiNoSite`) e a busca da home deles (barra e "Busca
 * avançada") guarda os números marcados como texto "2,3" (helpers `*Texto` no fim do arquivo).
 */
export type CampoQtd = 'suites_min' | 'garagems_min' | 'dormitorios_min' | 'banheiros_min';

/**
 * Modelos novos do /marketing/modelos: Aurora, Horizonte, Atelier, Panorama, Larissa, Metrópole, Internacional e Norte (topo 10–17)
 * — e quem usa só o hero deles (busca 13–20), que manda `dormitorios=2,3` para a listagem.
 */
export const qtdMultiNoSite = (site: any): boolean => {
    const topo = Number(site?.modelo_topo);
    const busca = Number(site?.modelo_busca);
    // 21/10: + Metrópole (#12, topo 15 · busca 18), Internacional (#12, topo 16 · busca 19) e Norte (#13, topo 17 · busca 20).
    // 22/10: + Campo (#14, topo 18 · busca 21).
    return (topo >= 10 && topo <= 18) || (busca >= 13 && busca <= 21);
};

// campo do estado → parâmetro da URL (os mesmos de sempre)
const PARAMS_QTD: Array<[CampoQtd, string]> = [
    ['suites_min', 'suites'],
    ['garagems_min', 'vagas'],
    ['dormitorios_min', 'dormitorios'],
    ['banheiros_min', 'banheiros'],
];

const campoLista = (campo: CampoQtd) => campo.replace('_min', '_lista');

const listaMarcada = (filters: any, campo: CampoQtd): number[] | null => {
    const lista = filters?.[campoLista(campo)];
    return Array.isArray(lista) && lista.length > 1 ? lista : null;
};

/** Números marcados no campo: a lista do Atelier ou o número de sempre. */
export const qtdMarcados = (filters: any, campo: CampoQtd): number[] => {
    const lista = listaMarcada(filters, campo);
    if (lista) return lista;
    const n = Number(filters?.[campo]) || 0;
    return n > 0 ? [n] : [];
};

/** Atelier: liga/desliga um número. Devolve o pedaço de `filters` do campo. */
export const alternarQtd = (filters: any, campo: CampoQtd, numero: number) => {
    const atuais = qtdMarcados(filters, campo);
    const novos = (atuais.includes(numero) ? atuais.filter((n) => n !== numero) : [...atuais, numero]).sort((a, b) => a - b);
    return { [campo]: novos[0] || 0, [campoLista(campo)]: novos.length > 1 ? novos : undefined };
};

/** Para os services de busca: a lista com 2+ marcados; senão o número de sempre. */
export const qtdParaBusca = (filters: any, campo: CampoQtd): number | number[] =>
    listaMarcada(filters, campo) || Number(filters?.[campo] || 0);

/** Valor do parâmetro da URL (e do SEO): "2,3" com a lista; senão o valor de sempre. */
export const qtdParaUrl = (filters: any, campo: CampoQtd) => listaMarcada(filters, campo)?.join(',') ?? filters?.[campo];

const juntarOu = (lista: Array<number | string>) => `${lista.slice(0, -1).join(', ')} ou ${lista[lista.length - 1]}`;

/** Texto do número: "2 ou 3", "1, 2 ou 3"; sem lista, o valor de sempre. */
export const qtdTexto = (filters: any, campo: CampoQtd) => {
    const lista = listaMarcada(filters, campo);
    return lista ? juntarOu(lista) : filters?.[campo];
};

/** O mesmo, lido direto da URL: "2,3" → "2 ou 3"; sem vírgula, o valor de sempre. */
export const qtdTextoDaUrl = (valor: any) => {
    const lista = String(valor ?? '').split(',').map((v) => v.trim()).filter(Boolean);
    return lista.length > 1 ? juntarOu(lista) : valor;
};

export const temListaQtd = (filters: any, campo: CampoQtd) => !!listaMarcada(filters, campo);

/**
 * Atelier: `dormitorios=2,3` da URL → `{ dormitorios_min: 2, dormitorios_lista: [2, 3] }` (só os
 * campos com 2+ números; com um só vale a leitura de sempre). Aceita o valor cru do router.query.
 */
export const qtdMultiDaUrl = (query: Record<string, unknown>): Record<string, any> => {
    const out: Record<string, any> = {};
    for (const [campo, param] of PARAMS_QTD) {
        const bruto = Array.isArray(query?.[param]) ? (query[param] as unknown[])[0] : query?.[param];
        const numeros = String(bruto ?? '')
            .split(',')
            .map((v) => parseInt(v, 10))
            .filter((n) => n > 0 && n <= 5);
        const unicos = Array.from(new Set(numeros)).sort((a, b) => a - b);
        if (unicos.length > 1) {
            out[campo] = unicos[0];
            out[campoLista(campo)] = unicos;
        }
    }
    return out;
};

/** Args `*_lista` do site-graphql para os campos que chegaram em lista (vazio sem lista). */
export const argsQtdLista = (valores: Record<'garagems' | 'dormitorios' | 'suites' | 'banheiros', unknown>): string =>
    Object.entries(valores)
        .filter(([, v]) => Array.isArray(v) && v.length > 0)
        .map(([campo, v]) => `,${campo}_lista: [${(v as unknown[]).map(Number).join(', ')}]`)
        .join('');

/** O número de sempre para o par min/max; lista vira 0 (vai pelos args `*_lista`). */
export const qtdEscalar = <T,>(valor: T | number[]): T | 0 => (Array.isArray(valor) ? 0 : valor);

// ---- HOME (busca dos heros): os números marcados num texto só, "2,3" ----

/** Os números dos botões; o 5 vale "5 ou mais" (como na lateral de /imoveis). */
export const QTD_NUMEROS = [1, 2, 3, 4, 5];

/** "2,3" → [2, 3]. Acima de 5 vira 5 (o Math.min de sempre do hook); repetidos e lixo saem. */
export const qtdListaDoTexto = (valor: unknown): number[] => {
    const numeros = String(valor ?? '')
        .split(',')
        .map((v) => parseInt(v, 10))
        .filter((n) => n > 0)
        .map((n) => Math.min(n, 5));
    return Array.from(new Set(numeros)).sort((a, b) => a - b);
};

/** Liga/desliga um número: ("2", 3) → "2,3"; ("2,3", 2) → "3". */
export const alternarQtdTexto = (valor: unknown, numero: number): string => {
    const atuais = qtdListaDoTexto(valor);
    const novos = atuais.includes(numero) ? atuais.filter((n) => n !== numero) : [...atuais, numero];
    return novos.sort((a, b) => a - b).join(',');
};

/** Valor do parâmetro da URL: "2,3", "2" ou '' (sem filtro). */
export const qtdTextoParaUrl = (valor: unknown): string => qtdListaDoTexto(valor).join(',');

/** "2 quartos", "2 ou 3 quartos", "5+ quartos", "1 quarto"; '' sem nada marcado. */
export const rotuloQtdTexto = (valor: unknown, singular: string, plural: string): string => {
    const lista = qtdListaDoTexto(valor).map((n) => (n === 5 ? '5+' : String(n)));
    if (!lista.length) return '';
    return `${lista.length > 1 ? juntarOu(lista) : lista[0]} ${lista.length === 1 && lista[0] === '1' ? singular : plural}`;
};

/**
 * URLSearchParams escreve a vírgula como %2C; a listagem monta a dela com vírgula crua
 * (`dormitorios=2,3`) — a home passa a sair igual.
 */
export const virgulaCrua = (query: string): string => query.replace(/%2C/gi, ',');
